/**
 * personMembership - which of the org's teams and departments a person is in.
 *
 * There is no per-person membership endpoint. `/org-people/people` carries
 * `teams` and `departments` on each row, but only for the LIST; the person
 * page fetches `/user-profile/user/uuid/:id`, which carries neither. The team
 * and department lists, however, each send their full `members` array - so
 * the answer is a filter over two lists the app already knows how to fetch.
 *
 * DELIBERATELY A PURE FUNCTION, not a hook: the matching is the part that can
 * be wrong, and the shapes it has to survive (a member keyed by `uuid`, by
 * `id`, or identifiable only by email) are exactly what a test can pin.
 */

import { normaliseAll } from '../../../shared/access/accessModel';

export interface OrgGroup {
  id: string;
  name: string;
  members?: any[];
  member_count?: number;
}

/** Who we are looking for. Any one of these matching is enough. */
export interface PersonIdentity {
  /** The route's uuid, and `user_info.id`. */
  uuid?: string;
  email?: string;
}

const sameId = (a: string, b?: string) => !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Is this person in this group?
 *
 * Matched through `accessModel.normalise`, so the four id keys the backends
 * disagree on (`uuid`, `user_uuid`, `user_id`, `id`) collapse to one before
 * the comparison - and email is the last resort for a row that carries no id
 * the caller would recognise.
 */
export const isMemberOf = (group: OrgGroup, person: PersonIdentity): boolean =>
  normaliseAll(group.members).some(
    (m) => sameId(m.id, person.uuid) || sameId(m.email, person.email)
  );

/** The groups this person belongs to, in the order the backend listed them. */
export const groupsFor = (
  groups: OrgGroup[] | null | undefined,
  person: PersonIdentity
): OrgGroup[] => (groups || []).filter((group) => isMemberOf(group, person));

/**
 * The person's role in a group, when the backend states one.
 *
 * '' when they are a plain member or the group does not record roles - the
 * pane renders no badge rather than asserting "Member" the backend never said.
 */
export const roleIn = (group: OrgGroup, person: PersonIdentity): string => {
  const row = (group.members || []).find((m: any) => {
    const [normalised] = normaliseAll([m]);
    return sameId(normalised.id, person.uuid) || sameId(normalised.email, person.email);
  });
  return String(row?.role || '');
};
